import React, { useEffect, useMemo, useState } from "react";
import {
  Building2,
  Check,
  Loader2,
  MapPin,
  RefreshCw,
  Save,
  Search,
  Shield,
  Users,
} from "lucide-react";

async function fetchJson(url, options = {}) {
  const res = await fetch(url, { credentials: "include", ...options });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) {
    throw new Error(json?.error || `HTTP ${res.status}`);
  }
  return json;
}

function asIdSet(ids) {
  return new Set((Array.isArray(ids) ? ids : []).map((id) => Number(id)).filter(Boolean));
}

export default function MultiLocationAssignments() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [draftIds, setDraftIds] = useState(new Set());
  const [query, setQuery] = useState("");
  const [companyQuery, setCompanyQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    setSaved(false);
    try {
      const data = await fetchJson("/api/admin/multilocation_assignments.php");
      const nextUsers = Array.isArray(data.users) ? data.users : [];
      setUsers(nextUsers);
      setCompanies(Array.isArray(data.companies) ? data.companies : []);
      setAssignments(data.assignments || {});

      const nextSelected = selectedUserId || nextUsers[0]?.id || null;
      setSelectedUserId(nextSelected);
      setDraftIds(asIdSet(data.assignments?.[nextSelected] || []));
    } catch (err) {
      setError(err.message || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((user) =>
      [user.nome, user.email, user.azienda, user.ruolo]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [users, query]);

  const filteredCompanies = useMemo(() => {
    const q = companyQuery.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((company) =>
      [company.nome, company.indirizzo, company.id]
        .filter((value) => value !== null && value !== undefined)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [companies, companyQuery]);

  const selectedUser = users.find((user) => Number(user.id) === Number(selectedUserId)) || null;

  const selectUser = (userId) => {
    setSelectedUserId(userId);
    setDraftIds(asIdSet(assignments?.[userId] || []));
    setSaved(false);
    setError("");
  };

  const toggleCompany = (companyId) => {
    setDraftIds((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else {
        next.add(companyId);
      }
      return next;
    });
    setSaved(false);
  };

  const save = async () => {
    if (!selectedUser) return;
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const aziende = Array.from(draftIds).sort((a, b) => a - b);
      await fetchJson("/api/admin/multilocation_assignments.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: selectedUser.id, azienda_ids: aziende }),
      });
      setAssignments((prev) => ({ ...prev, [selectedUser.id]: aziende }));
      setSaved(true);
    } catch (err) {
      setError(err.message || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-slate-500 flex items-center gap-2">
        <Loader2 size={18} className="animate-spin" />
        Caricamento multilocation...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Building2 size={18} /> Multilocation
          </h2>
          <p className="text-sm text-slate-500">
            Assegna a un admin le aziende che deve governare dalla dashboard multilocation.
          </p>
        </div>

        <button
          onClick={load}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw size={15} />
          Aggiorna
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {saved ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 flex items-center gap-2">
          <Check size={16} />
          Assegnazioni salvate.
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="border-b p-3">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="h-10 w-full rounded-lg border border-slate-300 pl-9 pr-3 text-sm outline-none focus:border-slate-700"
                placeholder="Cerca utente"
              />
            </div>
          </div>

          <div className="max-h-[560px] overflow-auto">
            {filteredUsers.map((user) => {
              const count = assignments?.[user.id]?.length || 0;
              const active = Number(user.id) === Number(selectedUserId);
              return (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => selectUser(user.id)}
                  className={[
                    "w-full border-b px-4 py-3 text-left transition hover:bg-slate-50",
                    active ? "bg-slate-900 text-white hover:bg-slate-900" : "bg-white text-slate-800",
                  ].join(" ")}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{user.nome || user.email}</div>
                      <div className={active ? "text-xs text-white/70 truncate" : "text-xs text-slate-500 truncate"}>
                        {user.email}
                      </div>
                      <div className={active ? "mt-1 text-xs text-white/70" : "mt-1 text-xs text-slate-500"}>
                        Base: {user.azienda || user.azienda_id || "-"}
                      </div>
                    </div>
                    <span className={active ? "rounded-full bg-white/15 px-2 py-1 text-xs" : "rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600"}>
                      {count} sedi
                    </span>
                  </div>
                </button>
              );
            })}

            {!filteredUsers.length ? (
              <div className="p-6 text-center text-sm text-slate-500">
                Nessun utente trovato.
              </div>
            ) : null}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b p-4">
            {selectedUser ? (
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Users size={15} />
                    Utente selezionato
                  </div>
                  <h3 className="mt-1 text-xl font-semibold text-slate-900">
                    {selectedUser.nome || selectedUser.email}
                  </h3>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                    <span>{selectedUser.email}</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-700">
                      <Shield size={12} />
                      {selectedUser.ruolo}
                    </span>
                  </div>
                </div>

                <button
                  onClick={save}
                  disabled={saving}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  Salva
                </button>
              </div>
            ) : (
              <div className="text-sm text-slate-500">Seleziona un utente.</div>
            )}
          </div>

          <div className="border-b p-3">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={companyQuery}
                onChange={(event) => setCompanyQuery(event.target.value)}
                className="h-10 w-full rounded-lg border border-slate-300 pl-9 pr-3 text-sm outline-none focus:border-slate-700"
                placeholder="Cerca azienda"
              />
            </div>
          </div>

          <div className="grid gap-2 p-4 md:grid-cols-2">
            {filteredCompanies.map((company) => {
              const id = Number(company.id);
              const checked = draftIds.has(id);
              return (
                <label
                  key={id}
                  className={[
                    "flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition",
                    checked
                      ? "border-indigo-200 bg-indigo-50"
                      : "border-slate-200 bg-white hover:bg-slate-50",
                  ].join(" ")}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleCompany(id)}
                    className="mt-1 h-4 w-4"
                  />
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">
                      {company.nome}
                    </div>
                    <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                      <Building2 size={12} />
                      ID {company.id}
                    </div>
                    {company.indirizzo ? (
                      <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                        <MapPin size={12} />
                        <span className="truncate">{company.indirizzo}</span>
                      </div>
                    ) : null}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
